import React from 'react';
import { isNullOrUndef } from 'chart.js/helpers';
import { isNull } from 'lodash';
import {
  AccessorNode,
  all,
  ArrayNode,
  ConstantNode,
  create,
  FunctionNode,
  MathNode,
  ObjectNode,
  OperatorNode,
  ParenthesisNode,
  SymbolNode,
} from 'mathjs';
import { mathGlobalScope } from 'common/constants';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { TCells } from 'pages/ValuationsAllocation/approaches/DiscountCashFlow/components/EffectiveTaxRateLedger/types';
import { getCurrencyValue, Logger } from 'utilities';
import * as customFormulas from '../../../common/formulas/math.js';
import ColoredSymbol from '../components/ColoredSymbol/ColoredSymbol';

const math = create(all);
math.import(customFormulas);

interface CellInfo {
  colSpan?: number;
  rowSpan?: number;
  isVisible?: boolean;
  isTitleOrHeader?: boolean;
  isTotal?: boolean;
  hidden?: boolean;
  value: any;
  getSymbols: () => Set<string>;
  providers: any[];
  getExprString: () => string;
}

interface CellsObject {
  [key: string]: CellInfo;
}

interface Sheet {
  cells: CellsObject;
  name?: string;
}

interface KeyCellMap {
  [key: string]: ICell;
}

/**
 * Get cell reference from the DOM
 */
export const getCellRef = (tableRef: React.RefObject<HTMLElement> | null, cellKey: string): HTMLElement | null => {
  const fullCellKey = `CELL-${cellKey}`;
  const cellRef = tableRef?.current?.querySelector(`#${fullCellKey}`) as HTMLElement | null;
  if (cellRef) {
    return cellRef;
  }
  return tableRef?.current?.querySelector(`td[data-cell-id=${fullCellKey}]`) as HTMLElement | null;
};

/**
 * Check if a cell has a column span
 */
export const hasCellColSpan = (cells: TCells, cellKey: string, cellRef?: HTMLElement | null): boolean => {
  const cellInfo = cells[cellKey];
  if (cellInfo) {
    const cellInfoColSpan = cellInfo.colSpan;
    return !isNullOrUndef(cellInfoColSpan) && cellInfoColSpan > 1;
  }
  if (cellRef) {
    const cellColSpan = cellRef.getAttribute('colspan');
    return !isNull(cellColSpan) && parseInt(cellColSpan || '1', 10) > 1;
  }
  return false;
};

/**
 * Check if a cell has a row span
 */
export const hasCellRowSpan = (cells: TCells, cellKey: string, cellRef?: HTMLElement | null): boolean => {
  const cellInfo = cells[cellKey];
  if (cellInfo) {
    const cellInfoRowSpan = cellInfo.rowSpan;
    return !isNullOrUndef(cellInfoRowSpan) && cellInfoRowSpan > 1;
  }
  if (cellRef) {
    const cellRowSpan = cellRef.getAttribute('rowspan');
    return !isNull(cellRowSpan) && parseInt(cellRowSpan || '1', 10) > 1;
  }
  return false;
};

/**
 * Get values to copy from cells
 */
export const getValuesToCopy = (
  cells: TCells,
  key: string | number,
  isColumn: boolean,
  exchangeRate?: number,
  units?: string
): string => {
  const comparisson = (item: string) =>
    isColumn ? item.startsWith(key.toString()) : parseInt(item.slice(1), 10) === key;

  const filteredKeys = Object.keys(cells).filter(item => comparisson(item));
  const selectedValues = filteredKeys.map(keyItem => cells[keyItem]);

  const formatCellCopyValue = (cell: (typeof selectedValues)[number]) => {
    if (cell.hidden) {
      return '';
    }
    const { value } = cell;
    if (value === null || value === undefined) {
      return '';
    }
    // Apply currency conversion for currency cells when an exchange rate is active
    if (
      exchangeRate &&
      exchangeRate !== 1 &&
      typeof value === 'number' &&
      cell.format?.style === 'currency'
    ) {
      return String(getCurrencyValue({ value, exchangeRate, units }));
    }
    return String(value);
  };

  let valuesToCopy = '';
  if (isColumn) {
    const filteredValues = selectedValues.filter(
      ({ isVisible, isTitleOrHeader, isTotal }) => isVisible !== false && !isTitleOrHeader && !isTotal
    );
    const values = filteredValues.map(formatCellCopyValue);
    valuesToCopy = values.join('\n');
  } else {
    const rowValues = selectedValues.map(formatCellCopyValue);
    // Use tab as separator for tabular data
    valuesToCopy = rowValues.join('\t');
  }
  return valuesToCopy;
};

/**
 * Copy values to clipboard
 */
export const copyToClipboard = (
  values: string,
  infoNotification: (message: string) => void,
  errorNotification: (message: string) => void
): void => {
  navigator.clipboard
    .writeText(values)
    .then(() => {
      infoNotification('Values copied to clipboard successfully!');
    })
    .catch(() => {
      errorNotification('Error copying values to clipboard');
    });
};

export class SymbolHandler {
  private readonly symbols: string[];

  private readonly node: MathNode | null;

  private readonly keyCellMap: KeyCellMap;

  private readonly list: (string | number | React.ReactElement)[];

  constructor(cell: ICell) {
    if (!cell.key || !cell.sheet) {
      this.symbols = [];
      this.node = null;
      this.keyCellMap = {};
      this.list = [];
      return;
    }
    const symbols = Array.from(cell.sheet.cells[cell.key].getSymbols());
    // exclude any symbols that are from the math global scope
    const mathSymbols = Object.keys(mathGlobalScope);
    this.symbols = symbols.filter(symbol => !mathSymbols.includes(symbol));

    const keyCellMap = Array.from(cell.sheet.cells[cell.key].providers).reduce((sofar, next) => {
      if (!next.getRelativeKeys || !cell.sheet) {
        return sofar; // Skip if provider does not have getRelativeKeys method
      }
      const keys = next
        .getRelativeKeys(cell.sheet)
        .flat()
        .filter(key => key) as string[];
      return keys.reduce(
        (map, key) => ({
          ...map,
          [key]: next,
        }),
        sofar
      );
    }, {} as KeyCellMap);
    const strExpr = cell.sheet.cells[cell.key].getExprString();

    try {
      const node = math.parse(strExpr.substring(1));
      this.node = node;
    } catch {
      // Handle parsing errors gracefully
      Logger.error('Error parsing expression in SymbolHandler', {
        sheet: cell.sheet?.name,
        key: cell.key,
        expr: strExpr,
      });
      // Set a fallback node
      this.node = null;
    }

    this.list = [];
    this.keyCellMap = keyCellMap;
  }

  insertSeparator(i: number, childList: any[]): void {
    if (i < childList.length - 1) {
      this.list.push(',');
    }
  }

  handleAccessorNode(node: AccessorNode): void {
    const symbol = node.toString();
    if (this.symbols.includes(symbol)) {
      this.list.push(
        React.createElement(ColoredSymbol, {
          symbol,
          value: this.keyCellMap[symbol]?.value as string | number,
          colorIndex: this.symbols.indexOf(symbol),
        })
      );
    }
  }

  handleArrayNode(node: ArrayNode): void {
    node.items.forEach((item, i) => {
      this.traverseAndReplaceSymbols(item);
      this.insertSeparator(i, node.items);
    });
  }

  handleFunctionNode(node: FunctionNode): void {
    this.list.push(`${node.fn.name}(`);
    node.args.forEach((child, i) => {
      this.traverseAndReplaceSymbols(child);
      this.insertSeparator(i, node.args);
    });
    this.list.push(')');
  }

  handleOperatorNode(node: OperatorNode): void {
    if (node.args.length === 2) {
      this.traverseAndReplaceSymbols(node.args[0]);
      this.list.push(node.op);
      this.traverseAndReplaceSymbols(node.args[1]);
    } else if (node.args.length === 1) {
      this.list.push(node.op);
      this.traverseAndReplaceSymbols(node.args[0]);
    } else {
      throw new Error('unhandled number of args for operator node');
    }
  }

  handleObjectNode(node: ObjectNode): void {
    this.list.push('{');
    const propertiesArray = Object.entries(node.properties);
    propertiesArray.forEach(([key, value], i) => {
      this.list.push(`"${key}": `);
      this.traverseAndReplaceSymbols(value);
      this.insertSeparator(i, propertiesArray);
    });
    this.list.push('}');
  }

  traverseAndReplaceSymbols(node: MathNode): void {
    if ((node as SymbolNode).isSymbolNode && this.symbols.includes((node as SymbolNode).name)) {
      this.list.push(
        React.createElement(ColoredSymbol, {
          symbol: (node as SymbolNode).name,
          value: this.keyCellMap[(node as SymbolNode).name]?.value as string | number,
          colorIndex: this.symbols.indexOf((node as SymbolNode).name),
        })
      );
    } else if ((node as OperatorNode).isOperatorNode) {
      this.handleOperatorNode(node as OperatorNode);
    } else if ((node as ConstantNode).isConstantNode) {
      this.list.push((node as ConstantNode).value);
    } else if ((node as FunctionNode).isFunctionNode) {
      this.handleFunctionNode(node as FunctionNode);
    } else if ((node as ArrayNode).isArrayNode) {
      this.list.push('[');
      this.handleArrayNode(node as ArrayNode);
      this.list.push(']');
    } else if ((node as AccessorNode).isAccessorNode) {
      this.handleAccessorNode(node as AccessorNode);
    } else if ((node as ObjectNode).isObjectNode) {
      this.handleObjectNode(node as ObjectNode);
    } else if ((node as ParenthesisNode).isParenthesisNode) {
      this.list.push('(');
      this.traverseAndReplaceSymbols((node as ParenthesisNode).content);
      this.list.push(')');
    } else {
      const nodeStr = node.toString();
      if (nodeStr in mathGlobalScope) {
        this.list.push(
          React.createElement(ColoredSymbol, {
            symbol: (node as SymbolNode).name,
            value: mathGlobalScope[(node as SymbolNode).name as keyof typeof mathGlobalScope],
            colorIndex: -1,
          })
        );
      } else {
        this.list.push(nodeStr);
      }
    }
  }

  getExpressionList(): (string | number | React.ReactElement)[] {
    if (!this.node) {
      // Return empty list if parsing failed
      return [];
    }
    this.traverseAndReplaceSymbols(this.node);
    return this.list;
  }
}
